import { Fragment, useCallback } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import {
  useHtmlToReact,
  type ElementTransform,
} from '@/components/Common/HtmlToReact';
import { ApprovalFormTable } from '@/components/Approval/@components/ApprovalFormTable';
import {
  formatShopManagerStartDate,
  type ShopManagerFeeSource,
} from '@/utils/approval/shopManagerFee';
import { ShopManagerFeeRoot } from './styles';

const COLUMN_WIDTHS = [
  56, 70, 200, 70, 100, 100, 130, 130, 100, 120, 80, 100, 100, 120, 120, 100,
  130, 80,
];

/** 근무시작일 뒤 금액 열 — 합계 행은 같은 순서로 `T_` 접두 */
const AMOUNT_KEYS = [
  'SALESAMT',
  'SALESAMT_PR',
  'REAL_AMT',
  'RATIO3',
  'HSUPAMT',
  'BEFOR_AMT',
  'RATIO1',
  'BEFOR_AMT_PR',
  'RATIO2',
  'BAMT_TOTAL',
  'AFTER_AMT',
  'ETC_AMT',
];

type TableProps = {
  source: ShopManagerFeeSource | null;
  t: TFunction;
};

/** 레거시 .form-group-box 안쪽 마크업(class·열 구성 동일) */
const ShopManagerFeeTable = ({ source, t }: TableProps) => {
  const items = source?.ITEM ?? [];
  const head = source?.HEAD ?? {};
  return (
    <div className="form-group">
      <table
        id="shopManager_table"
        className={`table table-orders table-bordered table-striped table-fixed no-margin text-center${
          items.length ? ' data-load' : ''
        }`}
      >
        <colgroup>
          {COLUMN_WIDTHS.map((width, index) => (
            <col key={index} span={1} style={{ width }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th colSpan={3}>{t('매장')}</th>
            <th colSpan={2}>{t('매니저')}</th>
            <th rowSpan={2}>{t('근무시작일')}</th>
            <th colSpan={2}>{t('매출')}</th>
            <th colSpan={2}>{t('실지급액')}</th>
            <th rowSpan={2}>{t('본사지원계')}</th>
            <th colSpan={4}>{t('공제전 수수료')}</th>
            <th rowSpan={2}>{t('공제계')}</th>
            <th rowSpan={2}>{t('공제후 수수료')}</th>
            <th rowSpan={2}>{t('추가공제')}</th>
          </tr>
          <tr>
            <th>{t('브랜드')}</th>
            <th>{t('코드')}</th>
            <th>{t('매장명')}</th>
            <th>{t('코드')}</th>
            <th>{t('성명')}</th>
            <th>{t('실판가')}</th>
            <th>{t('전년대비')}</th>
            <th>{t('지급액')}</th>
            <th>{t('매출대비')}</th>
            <th>{t('수수료')}</th>
            <th>{t('매출대비')}</th>
            <th>{t('전년수수료')}</th>
            <th>{t('매출대비')}</th>
          </tr>
        </thead>
        <tbody>
          {items.length ? (
            items.map((item, index) => (
              <tr key={index}>
                <td>{item.BRAND}</td>
                <td>{item.SHOP_CD}</td>
                <td>{item.SHOP_NM}</td>
                <td>{item.SM_CODE}</td>
                <td>{item.SM_NAME}</td>
                <td>{formatShopManagerStartDate(item.ST_DATE)}</td>
                {AMOUNT_KEYS.map((key) => (
                  <td key={key}>{item[key]}</td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={COLUMN_WIDTHS.length} className="text-left">
                {t('데이터가 없습니다.')}
              </td>
            </tr>
          )}
        </tbody>
        {items.length > 0 && (
          <tfoot>
            <tr>
              <th colSpan={6}>{t('합계')}</th>
              {AMOUNT_KEYS.map((key) => (
                <td key={key}>{head[`T_${key}`]}</td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};

/** attributes.content — 레거시 조회 화면은 이 HTML 만 그대로 출력 */
export const buildShopManagerFeeContentHtml = (
  source: ShopManagerFeeSource | null,
  t: TFunction,
): string =>
  renderToStaticMarkup(<ShopManagerFeeTable source={source} t={t} />);

const DROP_TAGS = new Set(['style', 'script', 'link']);

/** 저장 content(레거시 문서 포함) 그대로 — 표만 공통 양식 표로, 레거시 스크롤 래퍼는 벗김 */
export const ShopManagerFeeSnapshot = ({ content }: { content: string }) => {
  const transform = useCallback<ElementTransform>(
    ({ element, tagName, props, children }) => {
      if (DROP_TAGS.has(tagName)) return null;
      const key = props.key as string;
      if (tagName === 'div' && element.classList.contains('form-group')) {
        return <Fragment key={key}>{children}</Fragment>;
      }
      if (tagName === 'table') {
        return (
          <ApprovalFormTable
            key={key}
            id={props.id as string | undefined}
            className={props.className as string | undefined}
          >
            {children}
          </ApprovalFormTable>
        );
      }
      return undefined;
    },
    [],
  );
  const rendered = useHtmlToReact(content, { transform });
  return <ShopManagerFeeRoot>{rendered}</ShopManagerFeeRoot>;
};
